import { useState, useEffect } from "react";
import { T, Icon } from "../businessTheme";
import { supabase } from "../../lib/supabase";

const ROLES = ["Owner", "Admin", "Booking Manager", "Viewer"];

function RoleBadge({ role }) {
  const colors = {
    "Owner":           { bg: T.g50,      color: T.g700,     border: T.g100   },
    "Admin":           { bg: "#eff6ff",  color: "#1d4ed8",  border: "#bfdbfe" },
    "Booking Manager": { bg: "#fdf4ff",  color: "#7e22ce",  border: "#e9d5ff" },
    "Viewer":          { bg: T.n100,     color: T.n500,     border: T.n150   },
  };
  const c = colors[role] || colors.Viewer;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: c.color, background: c.bg, border: `1px solid ${c.border}`, borderRadius: 99, padding: "3px 10px" }}>
      {role}
    </span>
  );
}

/* ── Invite modal — requires the invitee to already have a personal JiraniApp account ── */
function InviteModal({ open, onClose, onInvite, businessId }) {
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("Viewer");
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null); // null | { found: true, profile } | { found: false }
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setPhone(""); setRole("Viewer"); setCheckResult(null); setError(""); }
  }, [open]);

  if (!open) return null;

  const handleCheckPhone = async () => {
    const cleaned = phone.replace(/\s/g, "");
    if (cleaned.length < 9) { setError("Enter a valid phone number."); return; }
    setError("");
    setChecking(true);

    const syntheticEmail = `${cleaned}@phone.jiraniapp.com`;
    const { data, error: qErr } = await supabase
      .from("profiles")
      .select("id, full_name, avatar_initials")
      .eq("email", syntheticEmail)
      .maybeSingle();

    if (qErr) { setError("Failed to check account. Try again."); setChecking(false); return; }
    setCheckResult(data ? { found: true, profile: data } : { found: false });
    setChecking(false);
  };

  const handleSendInvite = async () => {
    if (!checkResult?.found) return;
    onInvite(checkResult.profile, role);
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(15,17,23,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: "min(440px, 100%)", background: T.white, borderRadius: 18, padding: 26 }}>

        <h3 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: "0 0 16px" }}>
          Invite a team member
        </h3>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12.5, fontWeight: 600, color: T.n700, display: "block", marginBottom: 6 }}>
            Phone number (must have a JiraniApp personal account)
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={phone}
              onChange={e => { setPhone(e.target.value); setCheckResult(null); }}
              placeholder="e.g. 0712 345 678"
              style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: `1.5px solid ${T.n200}`, fontSize: 13.5, fontFamily: T.sans, outline: "none", boxSizing: "border-box" }}
            />
            <button
              onClick={handleCheckPhone}
              disabled={checking}
              style={{
                padding: "11px 16px", borderRadius: 10, border: "none",
                background: T.g500, color: T.white, fontSize: 13, fontWeight: 600,
                fontFamily: T.sans, cursor: checking ? "not-allowed" : "pointer", flexShrink: 0,
              }}>
              {checking ? "Checking..." : "Find"}
            </button>
          </div>
          {error && <p style={{ fontSize: 12, color: T.red, margin: "5px 0 0" }}>{error}</p>}
        </div>

        {checkResult && (
          <div style={{
            padding: "12px 14px", borderRadius: 10, marginBottom: 16,
            background: checkResult.found ? T.g50 : T.redBg,
            border: `1px solid ${checkResult.found ? T.g100 : T.redBorder}`,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <Icon
              name={checkResult.found ? "CheckCircle" : "AlertTriangle"}
              size={15}
              color={checkResult.found ? T.g600 : T.red}
              strokeWidth={2}
            />
            {checkResult.found ? (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.g800 }}>
                  {checkResult.profile.full_name} found
                </div>
                <div style={{ fontSize: 12, color: T.g600 }}>Personal JiraniApp account verified</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.red }}>No account found</div>
                <div style={{ fontSize: 12, color: "#991b1b" }}>
                  This person must create a personal JiraniApp account first.
                </div>
              </div>
            )}
          </div>
        )}

        {checkResult?.found && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: T.n700, display: "block", marginBottom: 8 }}>Role</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {ROLES.filter(r => r !== "Owner").map(r => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", borderRadius: 10,
                    border: `1.5px solid ${role === r ? T.g500 : T.n150}`,
                    background: role === r ? T.g50 : T.white,
                    cursor: "pointer", fontFamily: T.sans, fontSize: 13, color: T.n800, width: "100%", textAlign: "left",
                  }}>
                  {r}
                  {role === r && <Icon name="CheckCircle" size={15} color={T.g600} strokeWidth={2} />}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            style={{ padding: "10px 18px", borderRadius: 10, border: `1.5px solid ${T.n200}`, background: "transparent", color: T.n700, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
            Cancel
          </button>
          <button
            disabled={!checkResult?.found}
            onClick={handleSendInvite}
            style={{
              padding: "10px 22px", borderRadius: 10, border: "none",
              background: checkResult?.found ? T.g500 : T.n200,
              color: T.white, fontSize: 13, fontWeight: 600,
              fontFamily: T.sans, cursor: checkResult?.found ? "pointer" : "not-allowed",
            }}>
            Send invite
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Action menu per non-Owner member row ── */
function MemberActionMenu({ member, isPendingRemoval, onRemove }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: "none", border: "none", cursor: "pointer", color: T.n400, padding: 4 }}>
        <Icon name="ChevronDown" size={16} strokeWidth={2} />
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{
            position: "absolute", top: "100%", right: 0, marginTop: 4,
            background: T.white, border: `1px solid ${T.n150}`,
            borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            zIndex: 50, minWidth: 180, overflow: "hidden",
          }}>
            <button
              onClick={() => setOpen(false)}
              style={{ width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", fontSize: 12.5, color: T.n700, fontFamily: T.sans }}>
              Edit role
            </button>
            {isPendingRemoval ? (
              <div style={{ padding: "10px 14px", fontSize: 12.5, color: T.amber, fontFamily: T.sans }}>
                Removal scheduled
              </div>
            ) : (
              <button
                onClick={() => { onRemove(); setOpen(false); }}
                style={{ width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", fontSize: 12.5, color: T.red, fontFamily: T.sans }}>
                Remove from team
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Remove member modal — deferred + reason required ── */
function RemoveMemberModal({ open, member, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) { setReason(""); setAcknowledged(false); }
  }, [open, member?.id]);

  if (!open || !member) return null;

  const canSubmit = reason.trim().length >= 10 && acknowledged;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 500,
        background: "rgba(15,17,23,.5)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "min(480px, 100%)", background: T.white,
          borderRadius: 18, padding: 28, display: "flex", flexDirection: "column", gap: 18,
        }}>

        <div>
          <h3 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: "0 0 6px" }}>
            Remove team member
          </h3>
          <p style={{ fontSize: 13, color: T.n500, margin: 0, lineHeight: 1.6 }}>
            Removing <strong>{member.name}</strong> will be scheduled for the
            end of this billing cycle. Their completed bookings and history will
            be preserved.
          </p>
        </div>

        {member.pendingBookings > 0 && (
          <div style={{
            padding: "12px 14px", borderRadius: 10,
            background: T.amberBg, border: `1px solid ${T.amberBorder}`,
            display: "flex", gap: 10,
          }}>
            <Icon name="AlertTriangle" size={15} color={T.amber} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 12.5, color: "#92400e", margin: 0, lineHeight: 1.6 }}>
              This member has <strong>{member.pendingBookings} pending booking{member.pendingBookings !== 1 ? "s" : ""}</strong>.
              These will be completed before the removal takes effect.
              Booking data will remain intact.
            </p>
          </div>
        )}

        <div>
          <label style={{ fontSize: 12.5, fontWeight: 600, color: T.n700, display: "block", marginBottom: 7 }}>
            Reason for removal <span style={{ color: T.red }}>*</span>
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Describe why this member is being removed (minimum 10 characters)..."
            rows={4}
            style={{
              width: "100%", padding: "11px 14px", borderRadius: 10,
              border: `1.5px solid ${reason.trim().length > 0 && reason.trim().length < 10 ? T.red : T.n200}`,
              fontSize: 13, fontFamily: T.sans, outline: "none",
              boxSizing: "border-box", resize: "vertical", color: T.n900,
            }}
          />
          {reason.trim().length > 0 && reason.trim().length < 10 && (
            <p style={{ fontSize: 11.5, color: T.red, margin: "5px 0 0" }}>
              Please provide a reason of at least 10 characters.
            </p>
          )}
        </div>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={e => setAcknowledged(e.target.checked)}
            style={{ marginTop: 2, accentColor: T.g500, width: 15, height: 15, flexShrink: 0 }}
          />
          <span style={{ fontSize: 12.5, color: T.n600, lineHeight: 1.6 }}>
            I understand this removal will be processed at the end of the current
            billing cycle and that all booking records will be preserved.
          </span>
        </label>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              padding: "10px 18px", borderRadius: 10,
              border: `1.5px solid ${T.n200}`, background: "transparent",
              color: T.n700, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer",
            }}>
            Cancel
          </button>
          <button
            disabled={!canSubmit}
            onClick={() => onConfirm(member.id, reason)}
            style={{
              padding: "10px 22px", borderRadius: 10, border: "none",
              background: canSubmit ? T.red : T.n200,
              color: T.white, fontSize: 13, fontWeight: 600,
              fontFamily: T.sans, cursor: canSubmit ? "pointer" : "not-allowed",
            }}>
            Schedule removal
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Helper — last moment of the current calendar month ── */
function endOfCurrentMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();
}

/* ════════════════════════════════════════════════════════════ */

export default function MembersSection({ plan, account, userRole }) {
  const [members, setMembers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [removingMember, setRemovingMember] = useState(null);
  const [pendingRemovals, setPendingRemovals] = useState([]);

  useEffect(() => {
    const loadMembers = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("business_team_members")
        .select(`
          id,
          role,
          status,
          created_at,
          profiles:member_id (
            id,
            full_name,
            phone,
            avatar_initials
          )
        `)
        .eq("business_id", account.id)
        .eq("status", "active");

      if (!error && data) {
        setMembers(data.map(m => ({
          id: m.id,
          memberId: m.profiles?.id,
          name: m.profiles?.full_name ?? "Unknown",
          email: m.profiles?.phone ?? "",
          role: m.role,
          initials: m.profiles?.avatar_initials ?? "??",
          colorSet: { bg: "#eef8f3", text: "#155c36", border: "#d6f0e3" },
          lastActive: new Date(m.created_at).toLocaleDateString("en-KE"),
          pendingBookings: 0,
        })));
      }
      setLoading(false);
    };

    loadMembers();
  }, [account.id]);

  const handleInvite = async (profile, role) => {
    setInviteOpen(false);

    const { data: membership, error: memberError } = await supabase
      .from("business_team_members")
      .insert({
        business_id: account.id,
        member_id: profile.id,
        role: role,
        status: "pending",
        invited_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (memberError) {
      console.error("Failed to create team membership:", memberError);
      return;
    }

    await supabase.from("notifications").insert({
      recipient_id: profile.id,
      icon: "UserPlus",
      icon_color: "#7c3aed",
      title: "Team invitation",
      body: `${account.name} has invited you to join their team as ${role}.`,
      read: false,
      link_to: `/invitations/${membership.id}`,
      created_at: new Date().toISOString(),
    });

    await supabase.functions.invoke("send-team-invite-email", {
      body: {
        inviteeName: profile.full_name,
        businessName: account.name,
        role: role,
        invitationId: membership.id,
        inviteeProfileId: profile.id,
      },
    });

    setMembers(prev => [...prev, {
      id: membership.id,
      memberId: profile.id,
      name: profile.full_name,
      email: "",
      role: role,
      initials: profile.avatar_initials ?? profile.full_name.slice(0, 2).toUpperCase(),
      colorSet: { bg: T.n100, text: T.n500, border: T.n150 },
      lastActive: "Invited",
      pendingBookings: 0,
      status: "pending",
    }]);
  };

  const handleConfirmRemoval = async (memberId, reason) => {
    setPendingRemovals(prev => [...prev, memberId]);
    setRemovingMember(null);

    const { error } = await supabase
      .from("team_member_removals")
      .insert({
        business_id: account?.id,
        member_id: memberId,
        reason,
        scheduled_for: endOfCurrentMonth(),
        status: "scheduled",
        created_at: new Date().toISOString(),
      });

    if (error) {
      console.error("Failed to schedule removal:", error);
      setPendingRemovals(prev => prev.filter(id => id !== memberId));
    }
  };

  // Seat limit derived from plan.memberSeats; Infinity = no limit
  const unlimited     = plan.memberSeats === Infinity || plan.memberSeats === null;
  const seatsTotal    = unlimited ? null : plan.memberSeats;
  const seatsUsed     = members.length;
  const atSeatLimit   = !unlimited && seatsUsed >= seatsTotal;

  // Only Owner can invite members
  const canInvite = userRole === "Owner";

  return (
    <div>
      {/* ── Seat usage header row ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <p style={{ fontSize: 13.5, color: T.n400, margin: 0 }}>
          {seatsUsed}{unlimited ? "" : ` of ${seatsTotal}`} seat{seatsUsed !== 1 ? "s" : ""} used
          {" "}on your {plan.label} plan
          {unlimited && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: T.g700, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "2px 8px" }}>Unlimited</span>}
        </p>

        {/* Invite button — Owner only */}
        {canInvite && (
          <div style={{ position: "relative" }}>
            <button
              disabled={atSeatLimit}
              onClick={() => { if (!atSeatLimit) setInviteOpen(true); }}
              style={{
                padding: "10px 18px", borderRadius: 10, border: "none",
                background: atSeatLimit ? T.n200 : T.g500, color: T.white, fontSize: 13, fontWeight: 600,
                fontFamily: T.sans, cursor: atSeatLimit ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 7,
              }}>
              <Icon name="UserPlus" size={15} color={T.white} strokeWidth={2.2} />
              Invite member
            </button>
          </div>
        )}
      </div>

      {/* ── Seat limit nudge ── */}
      {atSeatLimit && canInvite && (
        <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 10, background: T.amberBg, border: `1px solid ${T.amberBorder}`, fontSize: 12.5, color: "#92400e", display: "flex", alignItems: "center", gap: 10 }}>
          <Icon name="AlertTriangle" size={14} color={T.amber} strokeWidth={2} />
          Seat limit reached. Upgrade your plan to add more team members.
        </div>
      )}

      <div style={{ border: `1px solid ${T.n150}`, borderRadius: 16, background: T.white, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 0.6fr", padding: "12px 20px", borderBottom: `1px solid ${T.n100}`, background: T.n50 }}>
          {["Member", "Role", "Last active", ""].map(h => (
            <span key={h} style={{ fontSize: 11, fontWeight: 700, color: T.n400, letterSpacing: "0.04em", textTransform: "uppercase" }}>{h}</span>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: "32px 20px", textAlign: "center" }}>
            <p style={{ fontSize: 13, color: T.n400, margin: 0 }}>Loading team…</p>
          </div>
        ) : members.length === 0 ? (
          <div style={{ padding: "32px 20px", textAlign: "center" }}>
            <p style={{ fontSize: 13, color: T.n400, margin: 0 }}>No team members yet.</p>
          </div>
        ) : (
          members.map((m, i) => (
            <div key={m.id} style={{
              display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 0.6fr", padding: "14px 20px", alignItems: "center",
              borderBottom: i < members.length - 1 ? `1px solid ${T.n100}` : "none",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: m.colorSet.bg, border: `1.5px solid ${m.colorSet.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, color: m.colorSet.text, flexShrink: 0 }}>
                  {m.initials}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: pendingRemovals.includes(m.id) ? T.n400 : T.n900, textTransform: "capitalize" }}>
                      {m.name}
                    </div>
                    {pendingRemovals.includes(m.id) && (
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: T.amber, background: T.amberBg, border: `1px solid ${T.amberBorder}`, borderRadius: 99, padding: "2px 8px" }}>
                        Removal scheduled
                      </span>
                    )}
                    {m.status === "pending" && (
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: T.n500, background: T.n100, border: `1px solid ${T.n150}`, borderRadius: 99, padding: "2px 8px" }}>
                        Invite pending
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: T.n400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.email}</div>
                </div>
              </div>

              <div><RoleBadge role={m.role} /></div>
              <div style={{ fontSize: 12.5, color: T.n500 }}>{m.lastActive}</div>

              <div style={{ textAlign: "right", position: "relative" }}>
                {m.role !== "Owner" && (
                  <MemberActionMenu
                    member={m}
                    isPendingRemoval={pendingRemovals.includes(m.id)}
                    onRemove={() => setRemovingMember(m)}
                  />
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvite={handleInvite}
        businessId={account.id}
      />
      <RemoveMemberModal
        open={!!removingMember}
        member={removingMember}
        onClose={() => setRemovingMember(null)}
        onConfirm={handleConfirmRemoval}
      />
    </div>
  );
}